/**
 * F9 · THE ONE share implementation in the funnel.
 *
 * Lifted verbatim out of S5 so sharing can live on its own screen (and be
 * reached again from the confirmation sequence) WITHOUT a second composite
 * builder, a second caption box or a second preview frame. The rasteriser is
 * still src/lib/share-card.ts; the card is still rendered by the ONE renderer.
 */
import { useEffect, useRef, useState } from "react";
import { Lede, Pill, inputClass } from "@/components/consumer/chrome";
import { DesignedCardFace } from "@/components/consumer/DesignedCardFace";
import { buildShareUrl, getOrCreateShareSlug, publishCardShare } from "@/services/share";
import { prepareShareImage, shareCard, SHARE_WORDMARK, type ShareChannel } from "@/lib/share-card";
import {
  DownloadIcon,
  InstagramIcon,
  MessagesIcon,
  TikTokIcon,
  WhatsAppIcon,
  XIcon,
} from "@/components/brand-icons";
import type { DesignedCard } from "@/lib/designed-card";
import { cn } from "@/lib/utils";

const SHARE_TILES: Array<{ id: ShareChannel; labelKey: string; Icon: (p: any) => JSX.Element }> = [
  { id: "Instagram", labelKey: "s5.share.tileStories", Icon: InstagramIcon },
  { id: "TikTok", labelKey: "s5.share.tileTikTok", Icon: TikTokIcon },
  { id: "WhatsApp", labelKey: "s5.share.tileWhatsApp", Icon: WhatsAppIcon },
  { id: "X", labelKey: "s5.share.tilePost", Icon: XIcon },
  { id: "Messages", labelKey: "s5.share.tileMessages", Icon: MessagesIcon },
  { id: "Save", labelKey: "s5.share.tileSave", Icon: DownloadIcon },
];

export interface ShareCardPanelProps {
  card: DesignedCard;
  styleName: string;
  styleId?: string | null;
  /** Content resolver from the S5 content section (keys unchanged). */
  t: (key: string) => string;
  /** Persist caption/format alongside the rest of the customisation. */
  onPresentationChange?: (next: { format: "story" | "post"; caption: string }) => void;
  initialFormat?: "story" | "post";
  initialCaption?: string;
}

export function ShareCardPanel({
  card,
  styleName,
  styleId,
  t,
  onPresentationChange,
  initialFormat = "story",
  initialCaption,
}: ShareCardPanelProps) {
  const [format, setFormat] = useState<"story" | "post">(initialFormat);
  // The caption asserts NOTHING about a code or a discount: neither exists.
  const [caption, setCaption] = useState(initialCaption ?? t("s5.share.defaultCaption"));
  // Opaque link id for the share target — never shown, never a referral code.
  const shareSlug = useRef(getOrCreateShareSlug()).current;
  const [copied, setCopied] = useState(false);
  const [sharedNote, setSharedNote] = useState<string | null>(null);
  const [sharing, setSharing] = useState<ShareChannel | null>(null);
  const shareFrameRef = useRef<HTMLDivElement>(null);
  const shareCardRef = useRef<HTMLDivElement>(null);

  function update(next: Partial<{ format: "story" | "post"; caption: string }>) {
    const merged = { format, caption, ...next };
    if (next.format !== undefined) setFormat(next.format);
    if (next.caption !== undefined) setCaption(next.caption);
    onPresentationChange?.(merged);
  }

  async function copyCaption() {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  /**
   * Build the composite AHEAD of the tap. navigator.share() only works inside
   * the tap's transient-activation window; rasterising in the handler blew it
   * on real phones and the share silently degraded to a link. Re-built when the
   * card (avatar/colour/orientation/framing) changes. Only when there IS a card
   * — a designed card without an avatar must never be shared as a blank card.
   *
   * DEBOUNCED, for two reasons: rapid colour/orientation/framing taps would
   * otherwise start a fresh ~2 s rasterisation per change, all competing for the
   * main thread on the device the user is tapping on; and the build must start
   * from the card the user has SETTLED on. Staleness is not possible either way
   * — prepareShareImage keys on the card, so a superseded build is discarded and
   * the tap reads only the latest card's image.
   */
  const cardKey = JSON.stringify(card);
  const preparedRef = useRef<Promise<File | null> | null>(null);
  useEffect(() => {
    if (!shareCardRef.current || !card.avatarUrl) {
      preparedRef.current = null;
      return;
    }
    // Drop the previous card's image immediately, so a tap in the gap can never
    // attach the image of a card the customer has already changed; shareCard()
    // then builds inside the tap for that one case.
    preparedRef.current = null;
    const timer = setTimeout(() => {
      if (!shareCardRef.current) return;
      preparedRef.current = prepareShareImage(
        { node: shareCardRef.current, brandLine: "cardmoji" },
        cardKey,
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [cardKey, card.avatarUrl]);


  async function onShare(channel: ShareChannel) {
    const shareUrl = buildShareUrl(shareSlug);
    setSharing(channel);
    try {
      if (channel !== "Save" && card.avatarUrl) {
        // Publish the public link target in PARALLEL with the sheet, never
        // ahead of it: awaiting a network round-trip here is part of what
        // pushed share() outside the tap window. The recipient opens the link
        // long after this resolves.
        void publishCardShare({
          code: shareSlug,
          avatarUrl: card.avatarUrl,
          colour: card.colour,
          cardStyle: styleId ?? null,
          orientation: card.orientation,
        });
      }

      const composite =
        shareCardRef.current && card.avatarUrl
          ? { node: shareCardRef.current, brandLine: "cardmoji" }
          : null;
      const outcome = await shareCard({
        channel,
        shareUrl,
        text: caption,
        title: "cardmoji",
        preparedFile: composite ? preparedRef.current : null,
        composite,
      });

      if (outcome === "shared" || outcome === "shared_link") {
        setSharedNote(t("s5.share.sharedNote").replace("{channel}", channel));
      } else if (outcome === "downloaded_copied") {
        setSharedNote(
          channel === "Save"
            ? "Image saved to your downloads."
            : "Image downloaded and link copied — attach it in your app.",
        );
      } else if (outcome === "copied") {
        setSharedNote(t("s5.share.linkCopiedNote"));
      } else if (outcome === "failed") {
        setSharedNote("Couldn't prepare the image. Try again.");
      } else {
        setSharedNote(null);
      }
    } catch {
      setSharedNote("Couldn't prepare the image. Try again.");
    } finally {
      setSharing(null);
    }
  }

  const shareFrameStyle: React.CSSProperties =
    format === "story"
      ? { aspectRatio: "9/16", width: "min(330px, 100%)", padding: "8% 7% 6%" }
      : { aspectRatio: "1/1", width: "min(300px, 100%)", padding: "9% 9% 9%" };

  return (
    <div data-testid="share-panel">
      <Lede className="mb-[18px] max-w-[42ch]">{t("s5.share.lede")}</Lede>

      <div className="mb-[18px] inline-flex gap-0.5 rounded-full bg-foreground/[0.07] p-[3px]">
        {(["story", "post"] as const).map((f) => (
          <Pill key={f} active={format === f} onClick={() => update({ format: f })} data-testid={`s5-format-${f}`}>
            {f === "story" ? t("s5.share.formatStory") : t("s5.share.formatPost")}
          </Pill>
        ))}
      </div>

      {/* PREVIEW EQUALS SHARED — mirrors the composite in src/lib/share-card.ts. */}
      <div className="mb-[22px] flex justify-center">
        <div
          ref={shareFrameRef}
          className="relative flex flex-col items-center justify-center overflow-hidden rounded-[18px] bg-paper shadow-[0_10px_28px_rgba(28,32,48,0.18)]"
          style={shareFrameStyle}
          data-testid="s5-share-frame"
        >
          <div
            ref={shareCardRef}
            className="flex-none"
            style={{ width: card.orientation === "landscape" ? "94%" : format === "story" ? "80%" : "68%" }}
          >
            <DesignedCardFace card={card} alt={styleName} className="w-full" />
          </div>
          <span className="mt-[4%] flex-none font-brand text-[10px] font-semibold leading-none text-ink">
            {SHARE_WORDMARK}
          </span>
        </div>
      </div>

      <label className="mb-2 block text-[14px] font-semibold">{t("s5.share.captionLabel")}</label>
      <textarea
        rows={3}
        value={caption}
        onChange={(e) => update({ caption: e.target.value })}
        className={cn(inputClass, "h-auto resize-y py-3.5")}
        data-testid="s5-caption"
      />
      <div className="mb-6 mt-2 flex items-center gap-2.5">
        <span className="text-[13px] text-foreground/55">{caption.length} {t("s5.share.charactersSuffix")}</span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={copyCaption}
          className="flex min-h-11 cursor-pointer items-center rounded-full border border-foreground/[0.18] bg-white px-4 text-[14px] font-semibold text-ink hover:border-ink"
          data-testid="s5-copy-caption"
        >
          {copied ? t("s5.share.copied") : t("s5.share.copyCaption")}
        </button>
      </div>

      <div className="mb-[14px] grid grid-cols-3 gap-2.5 md:grid-cols-6" data-testid="s5-share-tiles">
        {SHARE_TILES.map((tile) => (
          <button
            key={tile.id}
            type="button"
            onClick={() => onShare(tile.id)}
            disabled={sharing !== null}
            className="flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-2xl border border-foreground/[0.12] bg-white p-3.5 hover:border-ink disabled:opacity-60"
            data-testid={`s5-share-${tile.id.toLowerCase()}`}
          >
            <span className="grid h-[38px] w-[38px] place-items-center rounded-xl bg-ink text-paper">
              <tile.Icon />
            </span>
            <span className="text-[13px] font-semibold">
              {sharing === tile.id ? "Preparing…" : t(tile.labelKey)}
            </span>
          </button>
        ))}
      </div>

      {sharedNote ? (
        <div
          className="mb-[14px] rounded-2xl bg-foreground/[0.06] px-4 py-3 text-[14px] font-semibold"
          role="status"
          data-testid="s5-share-note"
        >
          {sharedNote}
        </div>
      ) : null}

      {/* The referral callout is deliberately ABSENT: there is no issued code
          and no configured discount to promise. Do not reinstate it without
          both a real code from the B7 issuer and configured program terms. */}
    </div>
  );
}

export default ShareCardPanel;
